import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import { toast } from "components/ui/use-toast";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";

import {
  LOCAL_DRAFT_SAVE_DEBOUNCE_MS,
  LOCAL_DRAFT_STORAGE_PREFIX,
  LOCAL_DRAFT_TTL_MS,
  LOCAL_DRAFT_VERSION,
} from "../../constants/local-draft";
import { LOCAL_DRAFT_MESSAGES } from "../../constants/messages";
import { type CreateGamificationFormData } from "../../validations/gamification.validation";

const ATTACHMENT_FIELDS = ["banner3x4", "banner16x9", "regulations"] as const;

const SEGMENTATION_FIELDS = ["segmentationFile", "segmentationCpfs"] as const;

export interface LocalDraft {
  version: number;
  savedAt: string;
  values: Partial<CreateGamificationFormData>;
  missingFiles: string[];
  segmentationOmitted: boolean;
}

interface UseLocalDraftResult {
  pendingDraft: LocalDraft | null;
  restore: () => void;
  discard: () => void;
  clear: () => void;
}

export function localDraftStorageKey(userKey: string): string {
  return `${LOCAL_DRAFT_STORAGE_PREFIX}:${userKey}`;
}

function resolveUserKey(): string | null {
  const user: { email?: unknown } | null = getUserFromLocalStorage();
  const email: unknown = user?.email;
  if (typeof email !== "string") return null;
  const normalized: string = email.trim().toLowerCase();
  return normalized === "" ? null : normalized;
}

function removeStoredDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    return;
  }
}

function fileName(value: unknown): string | null {
  return value instanceof File ? value.name : null;
}

function buildSnapshot(values: CreateGamificationFormData): LocalDraft {
  const copy: Record<string, unknown> = { ...values };
  const missingFiles: string[] = [];

  for (const field of ATTACHMENT_FIELDS) {
    const name: string | null = fileName(copy[field]);
    if (name) missingFiles.push(name);
    delete copy[field];
  }

  const segmentationOmitted: boolean = (values.segmentationCpfs ?? []).length > 0;
  for (const field of SEGMENTATION_FIELDS) {
    delete copy[field];
  }

  return {
    version: LOCAL_DRAFT_VERSION,
    savedAt: new Date().toISOString(),
    values: copy as Partial<CreateGamificationFormData>,
    missingFiles,
    segmentationOmitted,
  };
}

function isFreshDraft(savedAt: string): boolean {
  const savedTime: number = new Date(savedAt).getTime();
  if (Number.isNaN(savedTime)) return false;
  return Date.now() - savedTime <= LOCAL_DRAFT_TTL_MS;
}

function parseDraft(raw: string): LocalDraft | null {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object") return null;
  const draft = parsed as Partial<LocalDraft>;
  if (draft.version !== LOCAL_DRAFT_VERSION) return null;
  if (!draft.savedAt || !draft.values) return null;
  if (!isFreshDraft(draft.savedAt)) return null;
  return {
    version: LOCAL_DRAFT_VERSION,
    savedAt: draft.savedAt,
    values: draft.values,
    missingFiles: draft.missingFiles ?? [],
    segmentationOmitted: draft.segmentationOmitted ?? false,
  };
}

function readStoredDraft(key: string | null): LocalDraft | null {
  if (typeof window === "undefined") return null;
  removeStoredDraft(LOCAL_DRAFT_STORAGE_PREFIX);
  if (!key) return null;
  try {
    const raw: string | null = window.localStorage.getItem(key);
    if (!raw) return null;
    const draft: LocalDraft | null = parseDraft(raw);
    if (!draft) removeStoredDraft(key);
    return draft;
  } catch {
    removeStoredDraft(key);
    return null;
  }
}

export function useLocalDraft(
  form: UseFormReturn<CreateGamificationFormData>,
): UseLocalDraftResult {
  const userKey: string | null = useMemo(() => resolveUserKey(), []);
  const storageKey: string | null = userKey ? localDraftStorageKey(userKey) : null;
  const [pendingDraft, setPendingDraft] = useState<LocalDraft | null>(() =>
    readStoredDraft(storageKey),
  );
  const restoredRef = useRef<boolean>(false);

  const clear = useCallback((): void => {
    if (storageKey) removeStoredDraft(storageKey);
  }, [storageKey]);

  const { watch, formState, reset, getValues, setError } = form;
  const isDirty: boolean = formState.isDirty;

  useEffect(() => {
    if (!isDirty || !storageKey) return;
    let timer: number | undefined;
    const saveDraftIfStorageAllows = (values: unknown): void => {
      try {
        const snapshot: LocalDraft = buildSnapshot(
          values as CreateGamificationFormData,
        );
        window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
      } catch {
        return;
      }
    };
    const subscription = watch((values) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => saveDraftIfStorageAllows(values),
        LOCAL_DRAFT_SAVE_DEBOUNCE_MS,
      );
    });

    const flush = (): void => {
      window.clearTimeout(timer);
      saveDraftIfStorageAllows(getValues());
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.clearTimeout(timer);
      subscription.unsubscribe();
      window.removeEventListener("pagehide", flush);
    };
  }, [isDirty, storageKey, watch, getValues]);

  const restore = useCallback((): void => {
    if (!pendingDraft || restoredRef.current) return;
    restoredRef.current = true;
    reset(pendingDraft.values as CreateGamificationFormData, {
      keepDefaultValues: true,
    });
    if (pendingDraft.segmentationOmitted) {
      setError("segmentationFile", {
        type: "manual",
        message: LOCAL_DRAFT_MESSAGES.segmentationReimport,
      });
      toast({
        title: LOCAL_DRAFT_MESSAGES.segmentationRestoredTitle,
        description: LOCAL_DRAFT_MESSAGES.segmentationReimport,
        variant: "destructive",
      });
    }
    setPendingDraft(null);
  }, [pendingDraft, reset, setError]);

  const discard = useCallback((): void => {
    clear();
    setPendingDraft(null);
  }, [clear]);

  return { pendingDraft, restore, discard, clear };
}
